import { ShippingServiceModelType } from '@teinor/erp/types/company/config/shipping/shippingCompany/shippingService'
import { UserPermissionsSingle } from '../../../../../../../../../baseComponents/CreateRoutering/types'
import FormRenderer from '../../../../../../../../../forms/FormRenderer'
import { FormRender } from '../../../../../../../../../forms/FormRenderer/types'
import { TwinDictionary } from '../../../../../../../../../utils/globals/dictionary'
import { valueOrDefaultValue } from '../../../../../../../../../utils/globals/valueOrDefaultValue'
import useTwinTranslation from '../../../../../../../../../utils/hooks/useTwinTranslation'
import { getActiveCurrenciesDictionary } from '../../../../../../../../../utils/reducers/getters'
import { GroupVatJson } from '../../../../../../../../../utils/reducers/globals/groupVats'
import { useCallback, useState } from 'react'
import { changeErrorMessage } from '../../../../../../../../../utils/reducers/reduxDispatch'
import { returnClassOfSpanTemplate } from '../../../../../../../../../utils/globals/tailwind'

interface HeaderSingleConfigShippingServiceProps extends HeaderSingleConfigShippingServiceLogicProps {
    userPermissions: UserPermissionsSingle
    groupVats: GroupVatJson
}

const HeaderSingleConfigShippingService: React.FC<HeaderSingleConfigShippingServiceProps> = ({ dataService, userPermissions, groupVats, handleChangeHeader }) => {
    const { t } = useTwinTranslation()
    const { handleChangeHeaderMinMaxPrice, onBlurMinMaxPrice, minMaxPrice } = useHeaderSingleConfigShippingServiceLogic({ dataService, handleChangeHeader })
    const priceRangeTypes: TwinDictionary = {
        0: { name: t('money', 'Dinero')},
        1: { name: t('kg', 'kg')}
    }
    const serviceTypes: TwinDictionary = {
        0: { name: t('B2C', 'B2C')},
        1: { name: t('B2B', 'B2B')}
    }
    let fields: FormRender<ShippingServiceModelType> = [
        {
            cols: 3,
            className: ' gap-x-30',
            title: t('shippingServiceData', 'Datos del servicio'),
            elements: [
                {
                    name: 'name',
                    label: t('name', 'Nombre'),
                    component: 'InputWithLabelMargin',
                    type: 'text',
                    required: true,
                },
                {
                    name: 'CurrencyId',
                    label: t('currency', 'Moneda'),
                    component: 'CustomSelect',
                    items: getActiveCurrenciesDictionary(),
                    value: dataService?.CurrencyId,
                    onChange:(value)=> handleChangeHeader('CurrencyId', value), 
                    required: true
                },
                {
                    name: 'serviceType',
                    label: t('type', 'Tipo'),
                    component: 'CustomSelect',
                    
                    items: serviceTypes,
                    value: dataService?.serviceType,
                    required: true,
                },
                {
                    name: 'id',
                    component: 'InputHidden'
                }
            ],
        },
        {
            cols: 7,
            title: t('vats', 'Impuestos'),
            className: ' gap-x-30',
            elements: [
                {
                    name: 'priceVatIncluded',
                    label: t('priceVatIncluded', 'Precios con impuestos'),
                    component: 'CheckboxMargin',
                    className: returnClassOfSpanTemplate(2)
                },
                {
                    name: 'rangeVatIncluded',
                    label: t('rangeVatIncluded', 'Rangos con impuestos'),
                    component: 'CheckboxMargin',
                    className: returnClassOfSpanTemplate(2)
                },
                {
                    name: 'GroupVatId',
                    label: t('groupVat', 'Grupo de impuestos'),
                    component: 'CustomSelectWithSearchBar',
                    items: groupVats || {},
                    value: dataService?.GroupVatId,
                    className: returnClassOfSpanTemplate(3)
                },
            ]
        },
        {
            cols: 3,
            title: t('price', 'Precio'),
            className: ' gap-x-30',
            elements: [
                {
                    name: 'priceRangeType',
                    label: t('measureBy', 'Medir por'),
                    component: 'CustomSelect',
                    items: priceRangeTypes,
                    value: dataService?.priceRangeType,
                    onChange: (value) => handleChangeHeader('priceRangeType', value), 
                    required: true,
                },
                {
                    name: 'minPrice',
                    label: t('minPrice', 'Precio mínimo'),
                    component: 'InputPrice',
                    value: minMaxPrice?.minPrice,
                    CurrencyId: dataService?.CurrencyId,
                    onChange: (value) => handleChangeHeaderMinMaxPrice('minPrice', value), 
                    onBlur: (e) => onBlurMinMaxPrice('minPrice', e.currentTarget.value),
                    required: true,
                },
                {
                    name: 'maxPrice',
                    label: t('maxPrice', 'Precio máximo'),
                    component: 'InputPrice',
                    value: minMaxPrice?.maxPrice || 0,
                    onChange: (value) => handleChangeHeaderMinMaxPrice('maxPrice', value), 
                    onBlur: (e) => onBlurMinMaxPrice('maxPrice', e.currentTarget.value),
                    CurrencyId: dataService?.CurrencyId,
                },
            ]
        }
    ]
    if (dataService) {
        fields = valueOrDefaultValue(fields, userPermissions, dataService)
    }

    return (
        <div className='flex justify-between items-center'>
            <FormRenderer className='grid-cols-3 grid_mincontent gap-x-40' sections={fields} />
        </div>
    )
}

interface HeaderSingleConfigShippingServiceLogicProps {
    dataService: TwinDictionary | null
    handleChangeHeader: (field: string, val: string) => void
}   
type MinMaxPriceTy = {
    minPrice: number
    maxPrice: number | null
}

const useHeaderSingleConfigShippingServiceLogic = ({dataService, handleChangeHeader}: HeaderSingleConfigShippingServiceLogicProps) => {
    const [minMaxPrice, setMinMaxPrice] = useState < MinMaxPriceTy>({minPrice: dataService?.minPrice, maxPrice: dataService?.maxPrice})
    const {t} = useTwinTranslation()
    const handleChangeHeaderMinMaxPrice = useCallback((field: string, value: string) => {
        setMinMaxPrice((old)=>{return {...old, [field]:value }})
    }, [])
    const onBlurMinMaxPrice = useCallback((field: string, value: string) => {
        if (String(minMaxPrice.minPrice) === '') {
            minMaxPrice.minPrice = 0
        }
        if (String(minMaxPrice.maxPrice) === '') {
            minMaxPrice.maxPrice = null
        }
        if ((minMaxPrice.maxPrice && parseFloat(String(minMaxPrice.minPrice)) <= parseFloat(String(minMaxPrice.maxPrice))) || !minMaxPrice.maxPrice ){
            handleChangeHeader(field, value)
        } else {
            setMinMaxPrice((old)=>{return{...old, [field]: dataService?.[field]}})
            changeErrorMessage(t('checkMinMaxPrice', 'Revisa el precio mínimo y máximo'))
        }
    }, [dataService, handleChangeHeader, t, minMaxPrice])

    return { handleChangeHeaderMinMaxPrice, onBlurMinMaxPrice, minMaxPrice }
}


export default HeaderSingleConfigShippingService